import type { Dispatch } from '@reduxjs/toolkit'
import type { Business, User } from '@pbt/pbt-ui-components'

import type { PaymentData } from '~/components/dashboard/invoices/payment/constants/addPaymentDialogV2Constants'
import { PaymentFlowState } from '~/components/dashboard/invoices/payment/constants/addPaymentDialogV2Constants'
import type { InvoiceOrEstimate } from '~/types'
import { triggerErrorDoggo } from '~/utils/errorDoggoUtils'

import {
  trackFlowError,
  trackInvoiceRefreshed,
  trackSendToTerminalInitiated,
  trackSendToTerminalInitiatedSuccess,
  trackTerminalFailure,
} from './paymentDialogTrackingUtils'
import { refreshInvoiceAndBalanceHistory } from './paymentDialogUtils'
import { getTerminalInitiationFailureMessage } from './paymentErrorMapping'

interface HandleHardFailureParams {
  accountCreditsApplied: boolean
  backendErrorMessage: string
  businessId: string
  currentBusiness?: Business | null
  currentPaymentData: PaymentData
  currentUser?: User | null
  dispatch: Dispatch<any>
  errorMessage: string
  flowStartedAt?: number
  invoice: InvoiceOrEstimate
  paymentDialogDispatch: React.Dispatch<any>
}

function handleTerminalHardFailure({
  invoice,
  businessId,
  currentPaymentData,
  accountCreditsApplied,
  flowStartedAt,
  errorMessage,
  backendErrorMessage,
  paymentDialogDispatch,
  dispatch,
  currentUser,
  currentBusiness,
}: HandleHardFailureParams): void {
  paymentDialogDispatch({ type: 'CANCEL_TRANSACTION' })
  const durationMs =
    flowStartedAt != null ? Date.now() - flowStartedAt : undefined
  trackTerminalFailure({
    invoice,
    businessId,
    hardFailure: true,
    paymentMethod: currentPaymentData.paymentMethod,
    withAccountCredits: accountCreditsApplied,
    errorMessage: backendErrorMessage,
    amount: currentPaymentData.amountToCharge
      ? parseFloat(currentPaymentData.amountToCharge)
      : undefined,
    externalDeviceId: currentPaymentData.paymentTerminal || undefined,
    durationMs,
  })
  trackFlowError({
    invoice,
    businessId,
    errorSource: 'terminal_initiation',
    errorMessage: backendErrorMessage,
    paymentMethod: currentPaymentData.paymentMethod,
  })
  triggerErrorDoggo(
    dispatch,
    backendErrorMessage,
    500,
    currentUser,
    currentBusiness,
  )
  paymentDialogDispatch({
    type: 'SET_PAYMENT_ERROR',
    payload: {
      message: errorMessage,
      severity: 'error',
      status: 500,
    },
  })
}

interface InitiateTerminalSaleDependencies {
  accountCreditsApplied: boolean
  businessId: string
  currentBusiness?: Business | null
  currentUser?: User | null
  dispatch: Dispatch<any>
  error: any
  flowStartedAt?: number
  initiateTerminalSaleMutation: (options: {
    variables: {
      input: {
        amount: number
        externalDeviceId: string
        invoiceIds: string[]
      }
    }
  }) => Promise<any>
  invoice: InvoiceOrEstimate
  paymentDialogDispatch: React.Dispatch<any>
  setIsButtonActionLoading: (loading: boolean) => void
}

/**
 * Handles the terminal sale mutation with error handling.
 * Extracted to reduce cognitive complexity in useMemo.
 */
export const initiateTerminalSale = async (
  currentPaymentData: PaymentData,
  deps: InitiateTerminalSaleDependencies,
): Promise<void> => {
  const {
    paymentDialogDispatch,
    setIsButtonActionLoading,
    invoice,
    businessId,
    flowStartedAt,
    initiateTerminalSaleMutation,
    error,
    accountCreditsApplied,
    dispatch,
    currentUser,
    currentBusiness,
  } = deps
  try {
    const initiatedDurationMs =
      flowStartedAt != null ? Date.now() - flowStartedAt : undefined
    trackSendToTerminalInitiated({
      invoice,
      currentPaymentData,
      businessId,
      durationMs: initiatedDurationMs,
    })
    const result = await initiateTerminalSaleMutation({
      variables: {
        input: {
          amount: parseFloat(currentPaymentData.amountToCharge || '0'),
          externalDeviceId: currentPaymentData.paymentTerminal || '',
          invoiceIds: invoice.id.split(','),
        },
      },
    })

    if (!result.data?.initiateTerminalSale || error) {
      const errorMessage = getTerminalInitiationFailureMessage(
        accountCreditsApplied,
      )
      const backendErrorMessage = result.errors?.[0]?.message || errorMessage
      handleTerminalHardFailure({
        invoice,
        businessId,
        currentPaymentData,
        accountCreditsApplied,
        flowStartedAt,
        errorMessage,
        backendErrorMessage,
        paymentDialogDispatch,
        dispatch,
        currentUser,
        currentBusiness,
      })
    } else {
      const durationMs =
        flowStartedAt != null ? Date.now() - flowStartedAt : undefined
      trackSendToTerminalInitiatedSuccess({
        invoice,
        businessId,
        currentPaymentData,
        transactionId: result.data.initiateTerminalSale.id,
        externalDeviceId: result.data.initiateTerminalSale.externalDeviceId,
        durationMs,
      })
      paymentDialogDispatch({
        type: 'SET_STATE',
        payload: PaymentFlowState.PAYMENT_REQUEST,
      })
      paymentDialogDispatch({
        type: 'SET_PAYMENT_DATA',
        payload: {
          ...currentPaymentData,
          transactionId: result.data.initiateTerminalSale.id,
          externalDeviceId: result.data.initiateTerminalSale.externalDeviceId,
        },
      })
    }
  } catch (err) {
    const errorMessage = getTerminalInitiationFailureMessage(
      accountCreditsApplied,
    )
    const backendErrorMessage = (err as any)?.message || errorMessage
    handleTerminalHardFailure({
      invoice,
      businessId,
      currentPaymentData,
      accountCreditsApplied,
      flowStartedAt,
      errorMessage,
      backendErrorMessage,
      paymentDialogDispatch,
      dispatch,
      currentUser,
      currentBusiness,
    })
    if (accountCreditsApplied) {
      trackInvoiceRefreshed({
        invoice,
        businessId,
        trigger: 'transaction_hard_failure',
      })
      refreshInvoiceAndBalanceHistory(invoice, dispatch)
    }
  } finally {
    setIsButtonActionLoading(false)
  }
}
